/* Produktgalerie: Slider mit Scroll-Snap, Thumbnails und Punkte, Bilder je Variante über Gruppen im Alt-Text. */

class KgProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.slides = [...this.querySelectorAll('.gallery__slide')];
    this.buttons = [...this.querySelectorAll('[data-gallery-goto]')];
    this.counter = this.querySelector('[data-gallery-current]');
    this.total = this.querySelector('[data-gallery-total]');
    this.mode = this.dataset.variantImage || 'first';
    /* Videos und Modelle gehoeren zu keiner Sorte: immer sichtbar, immer hinten */
    this.videoIds = new Set(this.slides.filter((slide) => slide.dataset.mediaType !== 'image').map((slide) => slide.dataset.mediaId));
    try {
      this.variantGroups = JSON.parse(this.querySelector('[data-variant-groups]')?.textContent || '{}');
    } catch {
      this.variantGroups = {};
    }
    if (!this.main || !this.slides.length) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(button.dataset.galleryGoto, true));
    });
    /*
      Antippen macht das Bild in seiner Flaeche vollflaechig: Der Anschnitt rechts
      faellt weg. Nochmal antippen stellt ihn zurueck. Nur mobil, am Desktop gibt es
      keinen Anschnitt. Wischen loest keinen Klick aus, es stoert sich also nicht.

      Ein laufendes Video verhaelt sich dabei wie ein Bild. Angehalten wird es unten in
      seiner Leiste; liegt es still, deckt die Flaeche des Startknopfs das ganze Video ab
      und faengt den Tipp ab — der naechste gilt dann wieder dem Vergroessern.
    */
    this.main.addEventListener('click', (event) => {
      if (event.target.closest('button, a, input, .gallery__video-bar')) return;
      if (!window.matchMedia('(max-width: 1023px)').matches) return;
      this.classList.toggle('gallery--expanded');
    });
    this.main.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    });
    /* Nach einem programmatischen Sprung erst wieder auf den Beobachter hoeren, wenn das Scrollen fertig ist. */
    this.main.addEventListener('scrollend', () => {
      this.pendingId = null;
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.pendingId) return;
        const best = entries
          .filter((entry) => entry.isIntersecting && !entry.target.hidden)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best && best.intersectionRatio >= 0.6) this.setActive(best.target.dataset.mediaId);
      },
      { root: this.main, threshold: [0.6, 0.9] }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    /* Erst einsortieren, dann markieren: Sonst gilt das Video als letztes Medium */
    this.videoEinsortieren();
    this.markiereLetzte();
    /* Zwischen Mobil und Desktop wechselt auch die Stelle des Videos */
    this.breite = window.matchMedia('(max-width: 1023px)');
    this.breite.addEventListener('change', () => {
      this.videoEinsortieren();
      this.markiereLetzte();
    });

    const initial = this.dataset.active;
    if (initial) this.goTo(initial, false, 'instant');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.pendingTimer);
  }

  /* Sichtbare Slides in Anzeige-Reihenfolge (CSS order beruecksichtigt). */
  get visibleSlides() {
    return this.slides.filter((slide) => !slide.hidden).sort((a, b) => this.offsetOf(a) - this.offsetOf(b));
  }

  /*
    Das letzte sichtbare Bild rastet rechtsbuendig ein. Sonst bliebe beim
    angeschnittenen Streifen am Ende eine leere Spalte rechts stehen.
  */
  markiereLetzte() {
    const sichtbar = this.visibleSlides;
    this.slides.forEach((slide) => slide.classList.toggle('is-last', slide === sichtbar[sichtbar.length - 1]));
    /* Bei einem einzigen Bild gibt es nichts anzuschneiden */
    this.classList.toggle('gallery--single', sichtbar.length < 2);
  }

  /*
    Auf Mobil kann das Video an eine feste Stelle zwischen die Bilder. Die Bilder
    bekommen dafuer gerade Werte in ihrer bisherigen Reihenfolge, das Video einen
    ungeraden dazwischen. Ohne Angabe bleibt es hinten.
  */
  videoEinsortieren() {
    const videos = this.slides.filter((slide) => this.videoIds.has(slide.dataset.mediaId));
    if (!videos.length) return;
    const stelle = Number(this.dataset.videoPosition) || 0;
    if (!stelle || !window.matchMedia('(max-width: 1023px)').matches) {
      videos.forEach((slide) => (slide.style.order = '3'));
      return;
    }
    const bilder = this.visibleSlides.filter((slide) => !this.videoIds.has(slide.dataset.mediaId));
    bilder.forEach((slide, i) => (slide.style.order = String(i * 2)));
    videos.forEach((slide, i) => (slide.style.order = String((stelle - 1) * 2 - 1 + i)));
  }

  get activeSlide() {
    return this.slides.find((slide) => slide.classList.contains('is-active'));
  }

  /* Position eines Slides innerhalb des Scrollbereichs, unabhaengig davon, wo die Galerie auf der Seite liegt. */
  offsetOf(slide) {
    return slide.getBoundingClientRect().left - this.main.getBoundingClientRect().left + this.main.scrollLeft;
  }

  step(delta) {
    const ordered = this.visibleSlides;
    const index = ordered.indexOf(this.activeSlide);
    const next = Math.max(0, Math.min(ordered.length - 1, index + delta));
    if (ordered[next]) this.goTo(ordered[next].dataset.mediaId, true);
  }

  goTo(mediaId, smooth = true, behavior) {
    const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
    if (!slide || slide.hidden) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mode = behavior || (smooth && !reduced ? 'smooth' : 'instant');
    this.pendingId = slide.dataset.mediaId;
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => {
      this.pendingId = null;
    }, mode === 'smooth' ? 700 : 150);
    this.main.scrollTo({ left: this.offsetOf(slide), behavior: mode });
    this.setActive(mediaId);
  }

  setActive(mediaId) {
    const id = String(mediaId);
    this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === id));
    /*
      Videos folgen der Auswahl: Das gezeigte laeuft stumm an, jedes andere haelt an. Hier und
      nicht im Video selbst, weil die Galerie ohnehin schon weiss, was gerade zu sehen ist —
      ein zweiter Beobachter koennte zu einem anderen Schluss kommen als dieser.
    */
    this.slides.forEach((slide) => {
      const video = slide.querySelector('kg-video');
      if (!video) return;
      if (slide.dataset.mediaId === id) video.starteStumm?.();
      else video.anhalten?.();
    });
    if (this.counter) {
      const index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === id);
      this.counter.textContent = String(Math.max(0, index) + 1);
    }
    this.buttons.forEach((button) => {
      const active = button.dataset.galleryGoto === id;
      button.classList.toggle('is-active', active);
      if (button.getAttribute('role') === 'tab') button.setAttribute('aria-selected', String(active));
      if (active && button.classList.contains('gallery__thumb')) {
        /* Nur die Leiste selbst bewegen; scrollIntoView wuerde die ganze Seite mitziehen */
        const strip = button.parentElement;
        if (strip) {
          const box = button.getBoundingClientRect();
          const frame = strip.getBoundingClientRect();
          if (strip.scrollHeight > strip.clientHeight) {
            strip.scrollBy({ top: box.top - frame.top - (frame.height - box.height) / 2, behavior: 'smooth' });
          } else if (strip.scrollWidth > strip.clientWidth) {
            strip.scrollBy({ left: box.left - frame.left - (frame.width - box.width) / 2, behavior: 'smooth' });
          }
        }
      }
    });
  }

  /* Wird von product-form.js beim Variantenwechsel aufgerufen. */
  showVariant(variant) {
    if (!variant) return;
    const tokens = this.variantGroups[String(variant.id)] || [];
    const featuredId = variant.featured_media?.id ? String(variant.featured_media.id) : null;
    this.applyGroups(tokens, featuredId);
  }

  /*
    Regeln wie im Liquid: Hat das Produkt Rauten-Bilder, sind nur passende
    Bilder sichtbar, "#all" haengt hinten an, Bilder ohne Raute bleiben aus.
    Ohne Rauten-Bilder ist alles sichtbar. Das Variantenbild steht je nach
    Setting vorn (first), hinten (last) oder ist ausgeblendet (hide).
    Startbild ist immer das erste sichtbare Bild in Anzeige-Reihenfolge.
  */
  applyGroups(tokens, featuredId) {
    const groupsOf = (slide) => (slide.dataset.groups || '').split('|').filter(Boolean);
    const hasTags = this.slides.some((slide) => !this.videoIds.has(slide.dataset.mediaId) && groupsOf(slide).length);
    const visible = new Set();
    const trailing = new Set();
    this.slides.forEach((slide) => {
      const groups = groupsOf(slide);
      const id = slide.dataset.mediaId;
      if (!hasTags) {
        visible.add(id);
        return;
      }
      if (this.videoIds.has(id)) {
        visible.add(id);
        return;
      }
      const match = groups.some((group) => tokens.includes(group));
      if (match) visible.add(id);
      else if (groups.includes('all')) {
        visible.add(id);
        trailing.add(id);
      }
    });
    /* Passt keine Marke, bleibt nichts uebrig. Dann lieber alles zeigen als eine leere Galerie. */
    const bilder = this.slides.filter((slide) => !this.videoIds.has(slide.dataset.mediaId));
    if (hasTags && !bilder.some((slide) => visible.has(slide.dataset.mediaId))) {
      bilder.forEach((slide) => visible.add(slide.dataset.mediaId));
    }

    if (this.mode === 'hide' && featuredId && visible.has(featuredId) && visible.size > 1) visible.delete(featuredId);

    const apply = (element, id) => {
      element.hidden = !visible.has(id);
      let order = '';
      if (trailing.has(id)) order = '1';
      if (id === featuredId && this.mode === 'first') order = '-1';
      if (id === featuredId && this.mode === 'last') order = '2';
      if (this.videoIds.has(id)) order = '3';
      element.style.order = order;
    };
    this.slides.forEach((slide) => {
      apply(slide, slide.dataset.mediaId);
      slide.toggleAttribute('data-featured', slide.dataset.mediaId === featuredId);
    });
    this.buttons.forEach((button) => apply(button, button.dataset.galleryGoto));
    this.videoEinsortieren();
    this.markiereLetzte();
    if (this.total) this.total.textContent = String(visible.size);

    const target = this.visibleSlides[0];
    if (target) this.goTo(target.dataset.mediaId, false, 'instant');
  }
}

customElements.define('kg-product-gallery', KgProductGallery);
